import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CategoryLimitProgress } from "@/types/category-limit.type";
import { formatCurrencyWithCents } from "@/utils/currency";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import {
  categoryLimitFormSchema,
  CategoryLimitFormValues,
} from "./limit-form.schema";

interface CategoryLimitFormProps {
  categoryName: string;
  /** Existing limit for this category, if it already has one. */
  existingLimit?: CategoryLimitProgress;
  onSubmit: (values: CategoryLimitFormValues) => void;
  onClear?: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isClearing?: boolean;
}

const CategoryLimitForm: React.FC<CategoryLimitFormProps> = ({
  categoryName,
  existingLimit,
  onSubmit,
  onClear,
  onCancel,
  isSubmitting = false,
  isClearing = false,
}) => {
  const form = useForm<CategoryLimitFormValues>({
    resolver: zodResolver(categoryLimitFormSchema),
    defaultValues: {
      amount: existingLimit ? existingLimit.limit.toFixed(2) : "",
    },
  });

  const busy = isSubmitting || isClearing;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Monthly limit for {categoryName}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  autoFocus
                  disabled={busy}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {existingLimit
                  ? `You've spent ${formatCurrencyWithCents(
                      existingLimit.spent
                    )} in this category so far this month.`
                  : "We'll track this month's spending in this category against the amount you set."}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button
                className="w-full"
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
            <Button className="w-full" type="submit" disabled={busy}>
              {isSubmitting && <Loader2 className="animate-spin mr-2" />}
              {existingLimit ? "Save limit" : "Set limit"}
            </Button>
          </div>

          {existingLimit && onClear && (
            <Button
              className="w-full"
              type="button"
              variant="ghost"
              onClick={onClear}
              disabled={busy}
            >
              {isClearing && <Loader2 className="animate-spin mr-2" />}
              Remove limit
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};

export default CategoryLimitForm;
